import React from 'react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldLabel from '../../../../../components/stepper/field-components/FieldLabel';
import FieldError from '../../../../../components/stepper/field-components/FieldError';
import { RadioGroup } from '../../../../../components/stepper/field-components/FormRadioGroup';
import { Checkbox } from '../../../../../components/stepper/field-components/FormCheckboxGroup';
import {
  CustomFieldProps,
  findOtherFieldDependency,
} from 'components/stepper/field-components/QuestionnaireField';

const CheckboxGroupWithNoneOptionField = <T extends FieldValues>({
  question,
  dependencies,
  isRequired,
}: CustomFieldProps) => {
  const idStr = String(question.id) as Path<T>;

  const compositeInfo = findOtherFieldDependency(question, dependencies ?? []);

  const nestedFiledQuestion = compositeInfo.reduce((acc, { triggerValues }) => {
    return [...acc, ...triggerValues];
  }, [] as string[]);

  const radioNoneValue = question.none_option;
  const checkBoxValues = question.answers?.filter(
    (value) => !nestedFiledQuestion.includes(value) && value !== radioNoneValue,
  );

  const checkboxContainerClass =
    (checkBoxValues || []).length > 7
      ? 'grid grid-cols-2 gap-2.5'
      : 'flex flex-col gap-2.5';

  const { control } = useFormContext<T>();
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name: idStr, control });

  const selectRadioValue = () => {
    field.onChange([radioNoneValue]);
  };

  const onChangeCheckboxValue = (value: string, isChecked: boolean) => {
    const cur = new Set<string>(Array.isArray(field.value) ? field.value : []);
    cur.delete(radioNoneValue as string);
    isChecked ? cur.add(value) : cur.delete(value);
    field.onChange(Array.from(cur));
  };

  if (!checkBoxValues?.length) return null;

  return (
    <div id={field.name} className="flex flex-col">
      <div className="flex w-full flex-col">
        <div className="mb-2.5">
          <FieldLabel
            label={question.text}
            isRequired={isRequired}
            name={field.name}
          />
          <div className="text-med-gray-3 text-sm font-medium">
            {question.subtitle}
          </div>
        </div>

        {radioNoneValue && (
          <RadioGroup
            name={field.name}
            options={[{ value: radioNoneValue, label: radioNoneValue }]}
            selectedValue={field.value?.[0]}
            onChange={selectRadioValue}
            error={error?.message}
          />
        )}
        <div className={checkboxContainerClass}>
          {checkBoxValues.map((item) => {
            return (
              <Checkbox
                label={item}
                checked={field.value?.includes(item)}
                onChange={(isChecked) => onChangeCheckboxValue(item, isChecked)}
                name={field.name}
                error={!!error?.message}
              />
            );
          })}
        </div>
        <div className="flex flex-col gap-2.5 mt-2.5">
          {nestedFiledQuestion.map((item) => {
            return (
              <Checkbox
                label={item}
                checked={field.value?.includes(item)}
                onChange={(isChecked) => onChangeCheckboxValue(item, isChecked)}
                name={field.name}
                error={!!error?.message}
              />
            );
          })}
        </div>
        {error && <FieldError>{error.message}</FieldError>}
      </div>
    </div>
  );
};

export default CheckboxGroupWithNoneOptionField;
